import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { apiGet, apiPatch, apiDelete, type AuthUser } from "../api.js";
import { Badge, Button, Card } from "../components/ui/index.js";
import { NewKlienModal } from "../components/NewKlienModal.js";
import { statusLabel, t } from "../i18n.js";
import type { KlienListResponse } from "@dfa/types";

interface QueuePageProps {
  user: AuthUser;
}

type KlienItem = KlienListResponse["items"][number];

const CAN_CREATE_ROLES: Array<AuthUser["role"]> = ["scan_station", "agent", "owner", "super_admin"];

export function QueuePage({ user }: QueuePageProps) {
  const qc = useQueryClient();
  const [showNewKlien, setShowNewKlien] = useState(false);
  const [renameTarget, setRenameTarget] = useState<KlienItem | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<KlienItem | null>(null);

  const canEdit = CAN_CREATE_ROLES.includes(user.role);
  const canDelete = user.role === "owner" || user.role === "super_admin";

  const renameMutation = useMutation({
    mutationFn: (vars: { id: string; full_name: string }) =>
      apiPatch(`api/klien/${vars.id}`, { full_name: vars.full_name }),
    onSuccess: () => {
      setRenameTarget(null);
      qc.invalidateQueries({ queryKey: ["klien-queue"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`api/klien/${id}`),
    onSuccess: () => {
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ["klien-queue"] });
    },
  });

  // Include `uploaded` + all in-progress statuses so a brand-new klien (without
  // uploads yet) shows up immediately, and the user can track pipeline progress.
  const { data, isLoading, error, refetch, isFetching } = useQuery<KlienListResponse>({
    queryKey: ["klien-queue"],
    queryFn: () => apiGet<KlienListResponse>(
      "api/klien?status=uploaded,stitching,stitched,analyzing,analyzed,ready_for_review,approving"
    ),
    refetchInterval: 10_000,
  });

  const canCreate = CAN_CREATE_ROLES.includes(user.role) && user.franchisor_id !== null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-end gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-brand-900">{t.klienQueue}</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {data?.total ?? 0} klien · auto-refresh tiap 10 detik
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            iconLeft={
              <RefreshCw
                size={14}
                className={isFetching ? "animate-spin" : ""}
                strokeWidth={2}
              />
            }
          >
            {t.refresh}
          </Button>
          {canCreate && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowNewKlien(true)}
              iconLeft={<Plus size={14} strokeWidth={2.5} />}
            >
              Klien Baru
            </Button>
          )}
        </div>
      </div>

      {isLoading && <Card animate><div className="text-center text-slate-500 py-2">{t.loading}</div></Card>}
      {error && (
        <Card accent="danger" animate>
          <div className="text-sm text-tabrakan">
            Error: {error instanceof Error ? error.message : String(error)}
          </div>
        </Card>
      )}

      {data && data.items.length === 0 && (
        <Card animate>
          <div className="text-center py-8 space-y-3">
            <div className="text-slate-500">{t.empty}</div>
            {canCreate && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowNewKlien(true)}
                iconLeft={<Plus size={14} strokeWidth={2.5} />}
              >
                Buat klien pertama
              </Button>
            )}
          </div>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <div className="grid gap-3">
          {data.items.map((k, idx) => {
            const needs = k.finger_summary.needs_review_count;
            const minConf = k.finger_summary.min_confidence;
            const isLowConf = minConf !== null && minConf < 0.75;
            return (
              <motion.div
                key={k.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22, delay: idx * 0.03, ease: "easeOut" }}
              >
                <Link href={`/klien/${k.id}`} className="block">
                  <div className="card cursor-pointer hover:shadow-depth-2 hover:-translate-y-0.5 hover:border-brand-200 active:translate-y-0 active:shadow-depth-1 transition-all">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-brand-900 truncate">{k.full_name}</div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                          <span>Batch <strong className="text-slate-700">{k.batch_id}</strong></span>
                          <span className="text-slate-300">·</span>
                          <span>DOB <strong className="text-slate-700">{k.dob}</strong></span>
                          <span className="text-slate-300">·</span>
                          <Badge variant="info" className="!text-[10px] !py-0">{k.package_tier.toUpperCase()}</Badge>
                        </div>
                      </div>
                      <div className="flex items-start gap-1.5 shrink-0">
                        <Badge variant="neutral">{statusLabel(k.status)}</Badge>
                        {canEdit && (
                          <button
                            type="button"
                            title="Edit nama"
                            aria-label="Edit nama klien"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setRenameValue(k.full_name);
                              setRenameTarget(k);
                            }}
                          >
                            <Pencil size={15} strokeWidth={2} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            title="Hapus klien"
                            aria-label="Hapus klien"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-tabrakan hover:bg-red-50 transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDeleteTarget(k);
                            }}
                          >
                            <Trash2 size={15} strokeWidth={2} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs flex-wrap">
                      <span className="text-slate-600">
                        <strong className="text-brand-700">{k.finger_summary.approved}/{k.finger_summary.analyzed}</strong> approved · {k.finger_summary.analyzed}/10 dianalisa
                      </span>
                      {needs > 0 && (
                        <Badge variant="warning">⚠ {needs} {t.jumpToProblems}</Badge>
                      )}
                      {isLowConf && (
                        <Badge variant="danger">kepercayaan terendah: {((minConf ?? 0) * 100).toFixed(0)}%</Badge>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}

      <NewKlienModal
        open={showNewKlien}
        onClose={() => setShowNewKlien(false)}
        onCreated={() => {
          // Refresh queue so the new klien (status="uploaded") shows up.
          // Don't auto-navigate to Layer 4 — that page expects analyses to exist;
          // for a freshly created klien, the user's next step is to upload via
          // the upload UI (Bagian 2) accessible from the klien card.
          qc.invalidateQueries({ queryKey: ["klien-queue"] });
        }}
      />

      {/* Rename modal */}
      {renameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setRenameTarget(null)}>
          <div className="card w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-semibold text-brand-900">Edit nama klien</h2>
            <input
              autoFocus
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && renameValue.trim()) renameMutation.mutate({ id: renameTarget.id, full_name: renameValue.trim() });
              }}
              className="input w-full"
              placeholder="Nama klien"
            />
            {renameMutation.isError && (
              <div className="text-xs text-tabrakan">Gagal menyimpan: {renameMutation.error instanceof Error ? renameMutation.error.message : "error"}</div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setRenameTarget(null)}>Batal</Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!renameValue.trim() || renameMutation.isPending}
                onClick={() => renameMutation.mutate({ id: renameTarget.id, full_name: renameValue.trim() })}
              >
                {renameMutation.isPending ? "Menyimpan…" : "Simpan"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDeleteTarget(null)}>
          <div className="card w-full max-w-sm space-y-3 border-red-200" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-semibold text-tabrakan">Hapus klien?</h2>
            <p className="text-sm text-slate-600">
              Yakin hapus <strong className="text-slate-900">{deleteTarget.full_name}</strong>? Semua foto & hasil
              analisa ikut <strong>terhapus permanen</strong> dan tidak bisa dikembalikan.
            </p>
            {deleteMutation.isError && (
              <div className="text-xs text-tabrakan">Gagal menghapus: {deleteMutation.error instanceof Error ? deleteMutation.error.message : "error"}</div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(null)}>Batal</Button>
              <Button
                variant="danger"
                size="sm"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
              >
                {deleteMutation.isPending ? "Menghapus…" : "Hapus permanen"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
